import React, { useContext, useEffect, useState } from 'react';
import common from '../../common.module.scss';
import { getUsers, updateBlockUser, updateWritePoints } from '../../api/ClientsApi';
import { toast } from 'react-toastify';
import Pagination from '../pagination';
import { EmployeeContext } from '../../context/EmployeeContext.jsx';
import useIsMobile from "@/shared/lib/hooks/useIsMobile";
import { pluralize } from "@/shared/lib/pluralize/pluralize.js";
import { GiSettingsKnobs } from "react-icons/gi";
import FilterModal from "@/shared/ui/FiltersModal";

export default function ClientTable() {
    const { employee } = useContext(EmployeeContext);
    const role = employee?.role;

    const [clients, setClients] = useState([]);
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('');
    const [direction, setDirection] = useState('asc');
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [showModalFilters, setShowModalFilters] = useState(false);
    const [, setLoading] = useState(false);

    const isMobile = useIsMobile();

    useEffect(() => {
        fetchClients();
    }, [page, search, sortBy, direction]);

    const fetchClients = async () => {
        setLoading(true);
        try {
            const params = {
                page,
                size: 10,
                ...(search && { search }),
                ...(sortBy && { sortBy }),
                ...(sortBy && { direction })
            };
            const res = await getUsers(params);
            setClients(res.data.content || res.data);
            setTotalPages(res.data.totalPages || 1);
        } catch (err) {
            const errorMessage =
                err?.response?.data?.message || 'Ошибка при получении пользователей';
            toast.error(errorMessage);
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (field) => {
        if (sortBy !== field) {
            setSortBy(field);
            setDirection('desc');
        } else if (direction === 'desc') {
            setDirection('asc');
        } else {
            setSortBy('');
            setDirection('asc'); // сброс
        }
        setPage(0);
    };

    const renderSortArrow = (field) => {
        if (sortBy !== field) return '';
        return direction === 'asc' ? ' ↑' : ' ↓';
    };

    const toggleBlock = async (id) => {
        try {
            await updateBlockUser(id);
            setClients(prev =>
                prev.map(client =>
                    client.id === id ? { ...client, isBlocked: !client.isBlocked } : client
                )
            );
        } catch (err) {
            const errorMessage =
                err?.response?.data?.message || 'Ошибка при блокировке/разблокировке пользователя:';
            toast.error(errorMessage);
            console.error('Ошибка при блокировке/разблокировке пользователя:', err);
        }
    };

    const toggleWritePoints = async (id) => {
        try {
            await updateWritePoints(id);
            setClients(prev =>
                prev.map(client =>
                    client.id === id ? { ...client, isWritePoints: !client.isWritePoints } : client
                )
            );
        } catch (err) {
            const errorMessage =
                err?.response?.data?.message || 'Ошибка при изменении поля для списания баллов у пользователя:';
            toast.error(errorMessage);
            console.error('Ошибка при изменении поля для списания баллов у пользователя:', err);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setPage(newPage);
        }
    };

    const handleChangeSort = (field, dir) => {
        setSortBy(field);
        setDirection(dir);
        setPage(0);
    };

    const sortOptions = [
        { value: "id", label: "ID" },
        { value: "fio", label: "Имя и фамилия" },
        { value: "phone", label: "Телефон" },
        { value: "countGames", label: "Количество игр" },
        { value: "isBlocked", label: "Блокировка" }
    ];

    return (
        <div className={`container-fluid ${common.container}`}>
            {isMobile && (
                <h1 className={common.mobileHeading}>Клиенты</h1>
            )}
            <div className="mb-3 d-flex align-items-center">
                <div className="col-md-5 col-10">
                    <input
                        type="text"
                        placeholder="Поиск"
                        className={`form-control w-100 ${common.searchInput}`}
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(0);
                        }}
                    />
                </div>
                {isMobile && (
                    <button
                        type="button"
                        className={` ${common.iconButton}  ms-2`}
                        onClick={() => setShowModalFilters(true)}
                    >
                        <GiSettingsKnobs size={20} />
                    </button>
                )}
            </div>

            <table className={`table ${common.table}`}>
                <thead className={common.tableHeader}>
                <tr>
                    <th className={common.sortable} onClick={() => handleSort('id')}>
                        ID{renderSortArrow('id')}
                    </th>
                    <th className={common.sortable} onClick={() => handleSort('fio')}>
                        Имя и фамилия{renderSortArrow('fio')}
                    </th>
                    {!isMobile && (
                        <>
                            <th className={common.sortable} onClick={() => handleSort('phone')}>
                                Телефон{renderSortArrow('phone')}
                            </th>
                            <th className={common.sortable} onClick={() => handleSort('countGames')}>
                                Количество игр{renderSortArrow('countGames')}
                            </th>
                        </>
                    )}
                    <th className={common.sortable} onClick={() => handleSort('isWritePoints')}>
                        Списание баллов{renderSortArrow('isWritePoints')}
                    </th>
                    <th className={common.sortable} onClick={() => handleSort('isBlocked')}>
                        Блокировка{renderSortArrow('isBlocked')}
                    </th>
                </tr>
                </thead>
                <tbody>
                {clients.map((client, index) => (
                    <tr key={client.id} className={index % 2 === 1 ? common.altRow : ''}>
                        <td>{client.id}</td>
                        <td>
                            <p>{client.fio || "Не заполнено"}</p>
                            {isMobile && (
                                <>
                                    <p>{client.phone || "тел-на нет"}</p>
                                    <p className={"opacity-50"}>
                                        {client.countPlayedGames} {pluralize(client.countPlayedGames, ['игра', 'игры', 'игр'])}
                                    </p>
                                </>
                            )}
                        </td>
                        {!isMobile && (
                            <>
                                <td>{client.phone || "Не заполнено"}</td>
                                <td>{client.countPlayedGames}</td>
                            </>
                        )}
                        <td>
                            <div className="form-check form-switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={client.isWritePoints}
                                    onChange={() => toggleWritePoints(client.id)}
                                    disabled={role === "ADMIN" || role === "MASTER"}
                                />
                            </div>
                        </td>
                        <td>
                            <div className="form-check form-switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={client.isBlocked}
                                    onChange={() => toggleBlock(client.id)}
                                    disabled={role === "ADMIN" || role === "MASTER"}
                                />
                            </div>
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>

            <Pagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />

            <FilterModal
                isOpen={showModalFilters}
                onClose={() => setShowModalFilters(false)}
                sortOptions={sortOptions}
                sortBy={sortBy}
                direction={direction}
                onChangeSort={handleChangeSort}
            />
        </div>
    );
}
